"use client";

import React from "react";
import { FaTimes } from "react-icons/fa";
import { useRouter } from "next/navigation";

export interface BannerNotification {
    id: string;
    tripId: number;
    reason: string;
    messageHeader: string;
    messageContent: string;
    createdAt: Date;
    variant?: "danger" | "warning" | "success" | "update"; // danger=red, warning=orange, success=green, update=request removal
    layout?: "full" | "contained";
    showGoToTrip?: boolean;
}

interface GlobalNotificationBannerProps {
    notifications: BannerNotification[];
    onDismiss: (id: string) => void;
}

const GlobalNotificationBanner: React.FC<GlobalNotificationBannerProps> = ({
    notifications,
    onDismiss,
}) => {
    const router = useRouter();

    const handleGoToTrip = (tripId: number, id: string) => {
        // Dismiss banner first
        onDismiss(id);
        // Navigate to trip view page
        router.push(`/trip/view?tripId=${tripId}`);
    };

    const getVariantClass = (variant?: string): string => {
        switch (variant) {
            case "success":
                return "banner-success";
            case "warning":
                return "banner-warning";
            case "update":
                return "banner-update";
            case "danger":
            default:
                return "banner-danger";
        }
    };

    const getLayoutClass = (layout?: string): string =>
        layout === "contained" ? "banner-contained" : "banner-full";

    if (notifications.length === 0) {
        return null;
    }

    return (
        <>
            <div className="banner-container">
                {notifications.map((notification) => {
                    const canGoToTrip =
                        notification.showGoToTrip !== false &&
                        notification.tripId > 0;

                    return (
                        <div
                            key={notification.id}
                            className={`global-notification-banner ${getVariantClass(notification.variant)} ${getLayoutClass(notification.layout)}`}
                            role="alert"
                            aria-live="assertive"
                            aria-atomic="true"
                        >
                            <div className="banner-icon">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="28"
                                    height="28"
                                    viewBox="0 0 28 28"
                                    fill="none"
                                >
                                    <path
                                        d="M14 28C6.2678 28 0 21.7322 0 14C0 6.2678 6.2678 0 14 0C21.7322 0 28 6.2692 28 14C28 21.7308 21.7322 28 14 28ZM14 7C13.6287 7 13.2726 7.1475 13.0101 7.41005C12.7475 7.6726 12.6 8.0287 12.6 8.4V15.4C12.6 15.7713 12.7475 16.1274 13.0101 16.3899C13.2726 16.6525 13.6287 16.8 14 16.8C14.3713 16.8 14.7274 16.6525 14.9899 16.3899C15.2525 16.1274 15.4 15.7713 15.4 15.4V8.4C15.4 8.0287 15.2525 7.6726 14.9899 7.41005C14.7274 7.1475 14.3713 7 14 7ZM14 21C14.3713 21 14.7274 20.8525 14.9899 20.59C15.2525 20.3274 15.4 19.9713 15.4 19.6C15.4 19.2287 15.2525 18.8726 14.9899 18.6101C14.7274 18.3475 14.3713 18.2 14 18.2C13.6287 18.2 13.2726 18.3475 13.0101 18.6101C12.7475 18.8726 12.6 19.2287 12.6 19.6C12.6 19.9713 12.7475 20.3274 13.0101 20.59C13.2726 20.8525 13.6287 21 14 21Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </div>
                            <div className="banner-content">
                                <div className="banner-header">
                                    {notification.messageHeader}
                                </div>
                                <div
                                    className="banner-message"
                                    dangerouslySetInnerHTML={{
                                        __html: notification.messageContent,
                                    }}
                                />
                            </div>
                            {canGoToTrip ? (
                                <button
                                    type="button"
                                    className="banner-button"
                                    onClick={() =>
                                        handleGoToTrip(
                                            notification.tripId,
                                            notification.id
                                        )
                                    }
                                    aria-label={`Go to trip ${notification.tripId}`}
                                >
                                    Go to Trip
                                </button>
                            ) : null}
                            <button
                                type="button"
                                className="banner-close"
                                onClick={() => onDismiss(notification.id)}
                                aria-label="Close critical issue notification"
                                style={{
                                    position: "absolute",
                                    top: 4,
                                    right: 4,
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>
                    );
                })}
            </div>

            <style jsx global>{`
                .banner-container {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    z-index: 10000;
                    display: flex;
                    flex-direction: column;
                    pointer-events: none;
                }

                .banner-container > * {
                    pointer-events: auto;
                }

                .global-notification-banner {
                    width: 100%;
                    height: 118px;
                    border-radius: 4px;
                    color: #000;
                    padding: 16px 24px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    animation: slideDownFromTop 0.4s
                        cubic-bezier(0.34, 1.56, 0.64, 1);
                    position: relative !important;
                }

                .global-notification-banner.banner-contained {
                    width: calc(100% - 32px);
                    max-width: 1480px;
                    min-height: 96px;
                    height: auto;
                    margin: 12px auto 0;
                }

                /* Danger variant (red) - default for blocked/cancelled */
                .global-notification-banner.banner-danger {
                    border: 1px solid #edc4c8;
                    background: #fff6f7;
                }

                [data-bs-theme="dark"]
                    .global-notification-banner.banner-danger,
                [data-theme="dark"] .global-notification-banner.banner-danger {
                    border: none;
                    background: #272727;
                }

                /* Success variant (green) - for unblocked/resumed */
                .global-notification-banner.banner-success {
                    border: 1px solid #edc4c8;
                    background: #edf8f0;
                }

                .global-notification-banner.banner-update {
                    border: 1px solid #edc4c8;
                    background: #fff6f7;
                }

                [data-bs-theme="dark"]
                    .global-notification-banner.banner-success,
                [data-theme="dark"] .global-notification-banner.banner-success {
                    border-radius: 4px;
                    border: none;
                    background: #1e1e1e;
                }

                /* Warning variant (orange/yellow) - for delayed */
                .global-notification-banner.banner-warning {
                    border: 1px solid #ffeeba;
                    background: #fff3cd;
                }

                @keyframes slideDownFromTop {
                    from {
                        transform: translateY(-100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }

                .banner-icon {
                    font-size: 24px;
                    flex-shrink: 0;
                    display: flex;
                    align-items: center;
                    color: #dc3545;
                }

                .banner-content {
                    flex: 1;
                    min-width: 0;
                }

                .banner-header {
                    color: #000;
                    font-family: "DM Sans";
                    font-size: 23px;
                    font-style: normal;
                    font-weight: 800;
                    line-height: normal;
                    margin-bottom: 4px;
                }

                .banner-message {
                    color: #000;
                    font-family: "DM Sans";
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: normal;
                    word-wrap: break-word;
                }

                .banner-message strong {
                    font-weight: 700;
                }

                [data-bs-theme="dark"] .global-notification-banner,
                [data-bs-theme="dark"]
                    .global-notification-banner
                    .banner-header,
                [data-bs-theme="dark"]
                    .global-notification-banner
                    .banner-message,
                [data-bs-theme="dark"]
                    .global-notification-banner
                    .banner-message
                    strong,
                [data-bs-theme="dark"]
                    .global-notification-banner
                    .banner-close,
                [data-theme="dark"] .global-notification-banner,
                [data-theme="dark"] .global-notification-banner .banner-header,
                [data-theme="dark"] .global-notification-banner .banner-message,
                [data-theme="dark"]
                    .global-notification-banner
                    .banner-message
                    strong,
                [data-theme="dark"] .global-notification-banner .banner-close {
                    color: #f9f5f5;
                }

                [data-bs-theme="dark"]
                    .global-notification-banner.banner-update,
                [data-theme="dark"] .global-notification-banner.banner-update {
                    border: none;
                    background: #272727;
                    color: #f9f5f5;
                }

                [data-bs-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-header,
                [data-bs-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-message,
                [data-bs-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-message
                    strong,
                [data-bs-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-close,
                [data-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-header,
                [data-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-message,
                [data-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-message
                    strong,
                [data-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-close {
                    color: #f9f5f5;
                }

                [data-bs-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-icon,
                [data-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-icon {
                    color: #b03a37;
                }

                [data-bs-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-button,
                [data-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-button {
                    border-color: #b03a37;
                    background-color: #b03a37;
                }

                [data-bs-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-button:hover,
                [data-bs-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-button:active,
                [data-bs-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-button:focus,
                [data-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-button:hover,
                [data-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-button:active,
                [data-theme="dark"]
                    .global-notification-banner.banner-update
                    .banner-button:focus {
                    border-color: #952f2d;
                    background-color: #952f2d;
                }

                .banner-button {
                    border-radius: 6px;
                    border: 1px solid var(--Theme-Danger, #dc3545);
                    background: var(--Theme-Danger, #dc3545);
                    color: white;
                    display: flex;
                    width: 188px;
                    height: 38px;
                    padding: 6px 12px;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                    margin-right: 32px;
                    font-weight: 400;
                    font-size: 16px;
                    cursor: pointer;
                    white-space: nowrap;
                    transition: background-color 0.2s;
                    font-family: "DM Sans", sans-serif;
                    flex-shrink: 0;
                }

                .global-notification-banner.banner-success .banner-button {
                    border-radius: 6px;
                    border-color: #059a2c;
                    background: #059a2c;
                }

                .global-notification-banner.banner-success .banner-icon {
                    color: #059a2c;
                }

                .banner-button:hover {
                    background-color: #c6404d;
                    border-color: #c6404d;
                }

                .banner-button:active {
                    background-color: #b73440;
                    border-color: #b73440;
                }

                .banner-button:focus {
                    outline: 2px solid #dc3545;
                    outline-offset: 2px;
                }

                .global-notification-banner.banner-success .banner-button:hover,
                .global-notification-banner.banner-success
                    .banner-button:active,
                .global-notification-banner.banner-success
                    .banner-button:focus {
                    border-color: #058b28;
                    background-color: #058b28;
                }

                [data-bs-theme="dark"]
                    .global-notification-banner.banner-success
                    .banner-button,
                [data-theme="dark"]
                    .global-notification-banner.banner-success
                    .banner-button {
                    border-color: #047a23;
                    background-color: #047a23;
                }

                [data-bs-theme="dark"]
                    .global-notification-banner.banner-success
                    .banner-button:hover,
                [data-bs-theme="dark"]
                    .global-notification-banner.banner-success
                    .banner-button:active,
                [data-bs-theme="dark"]
                    .global-notification-banner.banner-success
                    .banner-button:focus,
                [data-theme="dark"]
                    .global-notification-banner.banner-success
                    .banner-button:hover,
                [data-theme="dark"]
                    .global-notification-banner.banner-success
                    .banner-button:active,
                [data-theme="dark"]
                    .global-notification-banner.banner-success
                    .banner-button:focus {
                    border-color: #03661d;
                    background-color: #03661d;
                }

                [data-bs-theme="dark"]
                    .global-notification-banner.banner-success
                    .banner-icon,
                [data-theme="dark"]
                    .global-notification-banner.banner-success
                    .banner-icon {
                    color: #047a23;
                }

                [data-bs-theme="dark"]
                    .global-notification-banner.banner-danger
                    .banner-button,
                [data-theme="dark"]
                    .global-notification-banner.banner-danger
                    .banner-button {
                    border-color: #b03a37;
                    background-color: #b03a37;
                }

                [data-bs-theme="dark"]
                    .global-notification-banner.banner-danger
                    .banner-button:hover,
                [data-bs-theme="dark"]
                    .global-notification-banner.banner-danger
                    .banner-button:active,
                [data-bs-theme="dark"]
                    .global-notification-banner.banner-danger
                    .banner-button:focus,
                [data-theme="dark"]
                    .global-notification-banner.banner-danger
                    .banner-button:hover,
                [data-theme="dark"]
                    .global-notification-banner.banner-danger
                    .banner-button:active,
                [data-theme="dark"]
                    .global-notification-banner.banner-danger
                    .banner-button:focus {
                    border-color: #952f2d;
                    background-color: #952f2d;
                }

                [data-bs-theme="dark"]
                    .global-notification-banner.banner-danger
                    .banner-icon,
                [data-theme="dark"]
                    .global-notification-banner.banner-danger
                    .banner-icon {
                    color: #b03a37;
                }

                .banner-close {
                    background: transparent;
                    border: none;
                    color: #000;
                    font-size: 24px;
                    cursor: pointer;
                    padding: 4px;
                    line-height: 1;
                    opacity: 0.6;
                    transition: opacity 0.2s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 32px;
                    height: 32px;
                    border-radius: 4px;
                    position: absolute !important;
                    top: 2px !important;
                    right: 2px !important;
                }

                .banner-close:hover {
                    opacity: 1;
                    background-color: rgba(0, 0, 0, 0.05);
                }

                .banner-close:focus {
                    outline: 2px solid #000;
                    outline-offset: 2px;
                }

                /* Responsive styles for mobile */
                @media (max-width: 768px) {
                    .global-notification-banner {
                        padding: 12px 16px;
                        gap: 12px;
                        flex-wrap: wrap;
                    }

                    .banner-icon {
                        font-size: 20px;
                    }

                    .banner-header {
                        font-size: 16px;
                    }

                    .banner-message {
                        font-size: 14px;
                    }

                    .banner-button {
                        padding: 8px 16px;
                        font-size: 14px;
                        min-width: 100px;
                    }

                    .banner-close {
                        width: 44px;
                        height: 44px;
                        font-size: 20px;
                        position: absolute !important;
                        top: 2px !important;
                        right: 2px !important;
                    }
                }

                /* Ensure adequate touch targets on mobile */
                @media (max-width: 768px) {
                    .banner-button,
                    .banner-close {
                        min-height: 44px;
                        min-width: 44px;
                    }
                }
            `}</style>
        </>
    );
};

export default GlobalNotificationBanner;
